// react
import { useCallback } from 'react';

// mui
import {
  Box,
  ButtonBase,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';

// types
import type { FactStatus, FactVersion, ReviewFact } from '@karehero/models';

// helpers
import { formatDate, fromDateInput } from '@karehero/helpers/datetime';

// components
import { Icon } from '@/components/atoms/Icon/Icon';

export interface FactRowProps {
  fact: ReviewFact;
  /** The first row in a card has no top border. */
  isFirst?: boolean;
  /** Omit to render the row read-only. */
  onEdit?: (fact: ReviewFact) => void;
  /** Picks one version of a conflicting fact. */
  onPickVersion?: (fact: ReviewFact, version: FactVersion) => void;
}

const NOT_ANSWERED_LABEL = 'Not answered';
const STALE_HINT = 'This may be out of date.';
const CONFLICT_HINT = 'Two sources disagree. Pick one.';
const EDIT_SLOT_WIDTH = 32;

/** Statuses that still need something from the user. */
export const ATTENTION_STATUSES: readonly FactStatus[] = [
  'missing',
  'stale',
  'conflict',
];

const displayValue = (fact: ReviewFact): string | undefined =>
  fact.control === 'date' && fact.value
    ? formatDate(fromDateInput(fact.value), {
        omitDay: true,
        month: 'long',
        year: 'numeric',
      })
    : fact.value;

export const isAttentionNeeded = (status: FactStatus): boolean =>
  ATTENTION_STATUSES.includes(status);

const rowSurface = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  px: 2,
  py: 1.5,
  borderTop: 1,
  borderTopStyle: 'solid',
  borderColor: 'custom.neutral.200',
} as const;

const firstRowSurface = { borderTop: 0 } as const;
const attentionSurface = { bgcolor: 'custom.coral.50' } as const;

const FactStatusMarker = ({ status }: { status: FactStatus }) => {
  if (isAttentionNeeded(status)) {
    return (
      <Box sx={{ display: 'flex', color: 'custom.coral.600' }}>
        <Icon icon='exclamation-circle' fontSize={11} />
      </Box>
    );
  }
  if (status === 'inferred') {
    return (
      <Box sx={{ display: 'flex', color: 'custom.sunflower.400' }}>
        <Icon icon='sparkles' type='fas' fontSize={11} />
      </Box>
    );
  }
  return null;
};

const FactVersions = ({
  fact,
  onPick,
}: {
  fact: ReviewFact;
  onPick?: (fact: ReviewFact, version: FactVersion) => void;
}) => (
  <RadioGroup
    aria-label={fact.label}
    value=''
    onChange={(_, value) => {
      const version = fact.versions?.find((v) => v.value === value);
      if (version) onPick?.(fact, version);
    }}
    sx={{ pt: 1.5, gap: 1 }}
  >
    {fact.versions?.map((version) => (
      <FormControlLabel
        key={version.value}
        value={version.value}
        control={<Radio />}
        label={
          <Box>
            <Typography variant='body1'>{version.value}</Typography>
            <Typography variant='caption' sx={{ color: 'text.secondary' }}>
              {version.source}
            </Typography>
          </Box>
        }
        sx={{ m: 0, alignItems: 'flex-start' }}
      />
    ))}
  </RadioGroup>
);

const FactRowContent = ({
  fact,
  isEditable,
  onPickVersion,
}: {
  fact: ReviewFact;
  isEditable: boolean;
  onPickVersion?: (fact: ReviewFact, version: FactVersion) => void;
}) => {
  const isAttention = isAttentionNeeded(fact.status);
  const isConflict = fact.status === 'conflict';

  return (
    <>
      <Stack direction='row' spacing={2} sx={{ alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction='row' spacing={0.75} sx={{ alignItems: 'center' }}>
            <FactStatusMarker status={fact.status} />
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>
              {fact.label}
            </Typography>
          </Stack>
          {isConflict ? (
            <Typography variant='body2' sx={{ color: 'custom.coral.700' }}>
              {CONFLICT_HINT}
            </Typography>
          ) : (
            <Typography
              variant='h6'
              sx={{ color: isAttention ? 'custom.coral.700' : 'text.primary' }}
            >
              {displayValue(fact) || NOT_ANSWERED_LABEL}
            </Typography>
          )}
          {fact.status === 'stale' && (
            <Typography variant='caption' sx={{ color: 'custom.coral.700' }}>
              {fact.since ? `Since ${fact.since}.` : STALE_HINT}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            flexShrink: 0,
            width: EDIT_SLOT_WIDTH,
            pt: 0.5,
            color: isAttention ? 'custom.coral.600' : 'custom.neutral.400',
          }}
        >
          {isEditable && <Icon icon='pencil' fontSize={13} />}
        </Box>
      </Stack>
      {isConflict && <FactVersions fact={fact} onPick={onPickVersion} />}
    </>
  );
};

/** FactRow shows one fact's label, value and status in a review card. */
export const FactRow = ({
  fact,
  isFirst = false,
  onEdit,
  onPickVersion,
}: FactRowProps) => {
  const isAttention = isAttentionNeeded(fact.status);
  const surface = [
    rowSurface,
    isFirst && firstRowSurface,
    isAttention && attentionSurface,
  ];

  const handleEdit = useCallback(() => onEdit?.(fact), [fact, onEdit]);

  const content = (
    <FactRowContent
      fact={fact}
      isEditable={Boolean(onEdit)}
      onPickVersion={onPickVersion}
    />
  );

  return onEdit ? (
    <ButtonBase
      id={`fact-${fact.id}`}
      aria-label={`Edit ${fact.label}`}
      onClick={handleEdit}
      sx={surface}
    >
      {content}
    </ButtonBase>
  ) : (
    <Box id={`fact-${fact.id}`} sx={surface}>
      {content}
    </Box>
  );
};

FactRow.displayName = 'FactRow';
export default FactRow;
